import React, {useEffect, useState, useCallback} from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    MenuItem,
    Divider,
    FormControlLabel,
    useTheme,
    useMediaQuery,
    Chip,
    Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {Coffee, Public, Opacity, SignalCellularAlt, Undo as UndoIcon, Redo as RedoIcon} from '@mui/icons-material';
import Api from 'src/api';
import {useDispatch} from 'react-redux';
import {showNotification} from 'src/redux/reducers/notificationReducer';
import {showConfirm} from 'src/redux/reducers/confirmReducer';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import RichTextEditor from 'src/components/RichTextEditor';

const validationSchema = Yup.object({
    isim: Yup.string().required('Kahve adı zorunludur'),
    fiyat_v60: Yup.number().min(0),
    fiyat_100gr: Yup.number().min(0),
    fiyat_250gr: Yup.number().min(0),
});

const KahveYonetimi = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useDispatch();
    const [kahveler, setKahveler] = useState<any[]>([]);
    const [openModal, setOpenModal] = useState(false);
    const [draggedKahve, setDraggedKahve] = useState<any>(null);

    const fetchKahveler = async () => {
        try {
            const data = await Api.getPromise('/menu/yonetim/kahve-yonetim/');
            setKahveler(data.sort((a: any, b: any) => (a.sira || 0) - (b.sira || 0)));
        } catch (error) {
            console.error("Kahve listesi çekilemedi", error);
        }
    };

    useEffect(() => {
        fetchKahveler();
    }, []);

    const formik = useFormik({
        initialValues: {
            id: undefined,
            isim: '',
            bolge: '',
            islem: '',
            tadim_notlari: '',
            govde: 3,
            asidite: 3,
            fiyat_v60: 0,
            mevcut_v60: true,
            fiyat_100gr: 0,
            mevcut_100gr: true,
            fiyat_250gr: 0,
            mevcut_250gr: true,
            aciklama: '',
            sira: 0,
            aktif: true
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                if (values.id) {
                    await Api.putPromise(`/menu/yonetim/kahve-yonetim/${values.id}/`, values);
                } else {
                    const nextSira = kahveler.length > 0 ? Math.max(...kahveler.map(k => k.sira || 0)) + 1 : 1;
                    await Api.postPromise('/menu/yonetim/kahve-yonetim/', {...values, sira: nextSira});
                }
                dispatch(showNotification({message: "Kahve başarıyla kaydedildi.", severity: "success"}));
                setOpenModal(false);
                fetchKahveler();
            } catch (error) {
                dispatch(showNotification({message: "Kayıt sırasında hata oluştu.", severity: "error"}));
            }
        }
    });

    const handleDelete = (id: number) => {
        dispatch(showConfirm({
            message: "Bu kahveyi silmek istediğinize emin misiniz?",
            onConfirm: async () => {
                try {
                    await Api.deletePromise(`/menu/yonetim/kahve-yonetim/${id}/`, {});
                    dispatch(showNotification({message: "Kahve silindi.", severity: "success"}));
                    fetchKahveler();
                } catch (error) {}
            }
        }));
    };

    // DRAG AND DROP HANDLERS
    const handleDragStart = (e: React.DragEvent, kahve: any) => {
        setDraggedKahve(kahve);
        e.dataTransfer.setData('text/plain', '');
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    const handleDrop = async (e: React.DragEvent, targetId: number) => {
        e.preventDefault();
        if (!draggedKahve || draggedKahve.id === targetId) return;

        const items = [...kahveler];
        const draggedIdx = items.findIndex(k => k.id === draggedKahve.id);
        const targetIdx = items.findIndex(k => k.id === targetId);

        items.splice(draggedIdx, 1);
        items.splice(targetIdx, 0, draggedKahve);

        const reordered = items.map((item, idx) => ({...item, sira: idx + 1}));
        setKahveler(reordered);

        // Backend'e sıralamayı toplu olarak kaydet
        try {
            const payload = {
                siralamalar: reordered.map(k => ({id: k.id, sira: k.sira}))
            };
            await Api.postPromise('/menu/yonetim/kahve-yonetim/siralamayi_kaydet/', payload);
            dispatch(showNotification({message: "Sıralama güncellendi.", severity: "success"}));
        } catch (error) {
            dispatch(showNotification({message: "Sıralama kaydedilirken hata oluştu.", severity: "error"}));
            fetchKahveler();
        }
        
        setDraggedKahve(null);
    };

    return (
        <Box sx={{ mt: 1 }}>
            <Box sx={{
                mb: 3,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                p: 1,
                bgcolor: '#f8f9fa',
                borderRadius: 1
            }}>
                <Button 
                    variant="contained" 
                    color="primary"
                    startIcon={<AddIcon />} 
                    onClick={() => {
                        formik.resetForm();
                        setOpenModal(true);
                    }}
                    sx={{ minWidth: 200, height: 40 }}
                >
                    Yeni Kahve Ekle
                </Button>
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: 'none' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell width={40}></TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Sıra</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Kahve Adı</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Bölge / İşlem</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>V60</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>100gr</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>250gr</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>İşlemler</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {kahveler.map((k) => (
                            <TableRow 
                                key={k.id} 
                                draggable
                                onDragStart={(e) => handleDragStart(e, k)}
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleDrop(e, k.id)}
                                sx={{ 
                                    cursor: 'grab',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.01)' },
                                    opacity: draggedKahve?.id === k.id ? 0.5 : 1
                                }}
                            >
                                <TableCell><DragIndicatorIcon fontSize="small" color="action" /></TableCell>
                                <TableCell>{k.sira}</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>{k.isim}</TableCell>
                                <TableCell>
                                    <Typography variant="body2">{k.bolge}</Typography>
                                    <Typography variant="caption" color="textSecondary">{k.islem}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <PriceChip label={k.fiyat_v60} active={k.mevcut_v60} />
                                </TableCell>
                                <TableCell align="right">
                                    <PriceChip label={k.fiyat_100gr} active={k.mevcut_100gr} />
                                </TableCell>
                                <TableCell align="right">
                                    <PriceChip label={k.fiyat_250gr} active={k.mevcut_250gr} />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => { formik.setValues(k); setOpenModal(true); }}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={() => handleDelete(k.id)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: '24px' } }}>
                <form onSubmit={formik.handleSubmit}>
                    <DialogTitle sx={{ bgcolor: '#333', color: 'white', p: 3 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Coffee sx={{ color: '#d4a373' }} />
                            <Typography variant="h6" fontWeight={800}>
                                {formik.values.id ? 'Kahve Çekirdeği Düzenle' : 'Yeni Kahve Çekirdeği'}
                            </Typography>
                        </Stack>
                    </DialogTitle>
                    <DialogContent sx={{ p: 3, pt: 4 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={10}>
                                <TextField fullWidth label="Kahve Adı" name="isim" value={formik.values.isim} onChange={formik.handleChange} sx={{ mb: 3, mt: 3 }} />
                                <RichTextEditor label="Genel Açıklama" value={formik.values.aciklama}
                                                onChange={(html) => formik.setFieldValue('aciklama', html)} />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormControlLabel control={<Switch checked={formik.values.aktif} onChange={formik.handleChange} name="aktif" />} label="Aktif" />
                            </Grid>

                            <Grid item xs={12}><Divider><Chip label="Karakteristik Özellikler" size="small" variant="outlined" /></Divider></Grid>

                            <Grid item xs={12} sm={6}>
                                <Stack spacing={2.5}>
                                    <TextField fullWidth label="Bölge / Menşei" name="bolge" value={formik.values.bolge} onChange={formik.handleChange} InputProps={{ startAdornment: <Public sx={{ mr: 1, color: 'primary.main' }} /> }} />
                                    <TextField fullWidth label="İşleme Yöntemi" name="islem" value={formik.values.islem} onChange={formik.handleChange} InputProps={{ startAdornment: <Opacity sx={{ mr: 1, color: 'primary.main' }} /> }} />
                                </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Stack spacing={2.5}>
                                    <TextField fullWidth label="Tadım Notaları" name="tadim_notlari" value={formik.values.tadim_notlari} onChange={formik.handleChange} placeholder="Örn: Karpuz, Çikolata, Fındık" />
                                    <TextField select fullWidth label="Gövde Skoru" name="govde" value={formik.values.govde} onChange={formik.handleChange}>
                                        {[1, 2, 3, 4, 5].map(v => <MenuItem key={v} value={v}>{v}/5</MenuItem>)}
                                    </TextField>
                                    <TextField select fullWidth label="Asidite Skoru" name="asidite" value={formik.values.asidite} onChange={formik.handleChange}>
                                        {[1, 2, 3, 4, 5].map(v => <MenuItem key={v} value={v}>{v}/5</MenuItem>)}
                                    </TextField>
                                </Stack>
                            </Grid>

                            <Grid item xs={12}><Divider><Chip label="Fiyatlandırma ve Stok" size="small" variant="outlined" /></Divider></Grid>

                            <Grid item xs={12}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ bgcolor: 'grey.50', p: 2, borderRadius: '16px' }}>
                                    <PriceInput label="V60 Fincan" priceName="fiyat_v60" stockName="mevcut_v60" formik={formik} />
                                    <Divider orientation="vertical" flexItem />
                                    <PriceInput label="100gr Paket" priceName="fiyat_100gr" stockName="mevcut_100gr" formik={formik} />
                                    <Divider orientation="vertical" flexItem />
                                    <PriceInput label="250gr Paket" priceName="fiyat_250gr" stockName="mevcut_250gr" formik={formik} />
                                </Stack>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 0 }}>
                        <Button onClick={() => setOpenModal(false)} variant="outlined">İptal</Button>
                        <Button color="primary" type="submit" variant="contained" startIcon={<SaveIcon />}>Değişiklikleri Kaydet</Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

const PriceChip = ({label, active}: { label: any, active: boolean }) => (
    <Chip 
        label={active ? `${label} ₺` : "TÜKENDİ"} 
        size="small" 
        color={active ? "primary" : "error"} 
        variant={active ? "outlined" : "filled"}
        sx={{ fontWeight: 800, minWidth: 70 }}
    />
);

const PriceInput = ({label, priceName, stockName, formik}: any) => (
    <Box sx={{ flex: 1 }}>
        <Typography variant="caption" fontWeight={900} color="textSecondary" display="block" sx={{ mb: 1, textAlign: 'center' }}>{label.toUpperCase()}</Typography>
        <Stack spacing={1}>
            <TextField 
                size="small" 
                type="number" 
                label="Fiyat" 
                name={priceName} 
                value={formik.values[priceName]} 
                onChange={formik.handleChange} 
            />
            <FormControlLabel 
                sx={{ ml: 0.5 }}
                control={<Switch size="small" checked={formik.values[stockName]} onChange={formik.handleChange} name={stockName} />} 
                label={<Typography variant="caption" fontWeight={700}>Mevcut</Typography>} 
            />
        </Stack>
    </Box>
);

export default KahveYonetimi;
